import { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";

type ThemeProviderProps = {
    children: React.ReactNode;
    defaultTheme?: Theme;
    storageKey?: string;
};

type ThemeProviderState = {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    removeBackground: () => void;
};

const initialState: ThemeProviderState = {
    theme: "system",
    setTheme: () => null,
    removeBackground: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
    children,
    defaultTheme = "system",
    storageKey = "vite-ui-theme",
    ...props
}: ThemeProviderProps) {
    const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem(storageKey) as Theme) || defaultTheme);

    const updateBackground = () => {
        const app = window.document.getElementById("app");
        // Dark colours target `.background-light` under `html.dark`.
        app?.classList.remove("background-dark", "background-no-gradient");
        app?.classList.add("background-light");
    };

    const removeBackground = () => {
        const app = window.document.getElementById("app");
        app?.classList.remove("background-light", "background-dark");
        app?.classList.add("background-no-gradient");
    };

    const updateTheme = (nextTheme: Theme) => {
        const root = window.document.documentElement;

        root.classList.remove("light", "dark");
        root.classList.add(nextTheme === "dark" ? "dark" : "light");
    };

    useEffect(() => {
        updateTheme(theme);
        updateBackground();
    }, [theme]);

    const value = {
        theme,
        setTheme: (theme: Theme) => {
            localStorage.setItem(storageKey, theme);
            setTheme(theme);
            updateBackground();
            updateTheme(theme);
        },
        removeBackground,
    };

    return (
        <ThemeProviderContext.Provider {...props} value={value}>
            {children}
        </ThemeProviderContext.Provider>
    );
}

export const useTheme = () => {
    const context = useContext(ThemeProviderContext);

    if (context === undefined) throw new Error("useTheme must be used within a ThemeProvider");

    return context;
};
